/**
 * Gap Detection Service
 *
 * Identifies gaps between reservations and manages gap-filling strategies:
 * - Detects gaps between bookings
 * - Calculates optimal discounts to fill gaps
 * - Checks MinLOS rules and determines override eligibility
 * - Tracks gap filling success rates
 */

import { supabase } from './supabaseClient';
import type { GapFillingRule } from '../types/revenue';

type ReservationRecord = {
  id: string;
  room_type_id: string;
  webhotelier_room_code: string;
  check_in: string;
  check_out: string;
  rate: number;
  status: string;
};

type CalendarAvailabilityRow = {
  date: string;
  room_type_code: string | null;
  available_rooms: number;
  final_rate: number | null;
};

export interface ReservationGap {
  id: string;
  propertyId: string;
  roomTypeId: string;
  startDate: Date;
  endDate: Date;
  duration: number;
  suggestedDiscount: number;
  suggestedDiscountRate?: number;
  discountAmount: number;
  originalPrice: number;
  discountedPrice: number;
  status: 'identified' | 'filled' | 'ignored' | 'expired';
  strategy?: string;
  ruleAppliedId?: string;
  detectedAt?: Date;
  notes?: string;
}

export interface GapDetectionOptions {
  minGapDays?: number; // Minimum gap duration to consider (default: 1)
  maxGapDays?: number; // Maximum gap duration to consider (default: 7)
  lookAheadDays?: number; // How many days to scan ahead (default: 90)
  includeExpired?: boolean; // Include past gaps
}

export interface GapFillStrategy {
  gapId: string;
  suggestedDiscount: number;
  minDiscount: number;
  expectedRevenue: number;
  minLosOverrideNeeded: boolean;
  originalMinLos?: number;
  allowedMinLos?: number;
}

export class GapDetectionService {
  async expireStaleGaps(propertyId: string): Promise<number> {
    const todayStr = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('reservation_gaps')
      .update({ status: 'expired', updated_at: new Date().toISOString() })
      .eq('property_id', propertyId)
      .eq('status', 'identified')
      .lt('end_date', todayStr)
      .select('id');

    if (error) {
      console.error('[GapDetectionService] Error expiring stale gaps:', error);
      return 0;
    }

    const count = data?.length ?? 0;
    if (count > 0) {
      console.log(`[GapDetectionService] Expired ${count} stale gaps`);
    }
    return count;
  }

  async detectGaps(
    propertyId: string,
    roomTypeId?: string,
    options: GapDetectionOptions = {}
  ): Promise<{
    success: boolean;
    gaps?: ReservationGap[];
    error?: string;
  }> {
    const {
      minGapDays = 1,
      maxGapDays = 7,
      lookAheadDays = 90, // Default 90 days for gap detection
    } = options;

    try {
      await this.expireStaleGaps(propertyId);

      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + lookAheadDays);

      // First, get room types for this property to map webhotelier_room_code -> room_type_id (UUID)
      const { data: roomTypes } = await supabase
        .from('room_types')
        .select('id, webhotelier_code, name')
        .eq('property_id', propertyId);

      if (!roomTypes || roomTypes.length === 0) {
        console.log('[GapDetectionService] No room types found for property');
        return { success: true, gaps: [] };
      }

      // Create mapping from webhotelier_code to room type UUID
      const roomCodeToId = new Map<string, string>();
      for (const rt of roomTypes) {
        if (rt.webhotelier_code) {
          roomCodeToId.set(rt.webhotelier_code, rt.id);
        }
        // Also map by name as fallback
        if (rt.name) {
          roomCodeToId.set(rt.name, rt.id);
        }
      }

      // Anchor gaps on availability (sold-out days), not on reservation
      // boundaries — the PMS-aligned view also picks up blocked rooms /
      // manual overrides that have no reservation. A gap is a run of OPEN
      // dates strictly between two sold-out dates for the same room type.
      let calQuery = supabase
        .from('calendar_data')
        .select('date, room_type_code, available_rooms, final_rate')
        .eq('property_id', propertyId)
        .gte('date', today.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('room_type_code')
        .order('date');

      if (roomTypeId) {
        const roomType = roomTypes.find((rt) => rt.id === roomTypeId);
        if (roomType?.webhotelier_code) {
          calQuery = calQuery.eq('room_type_code', roomType.webhotelier_code);
        } else {
          return { success: true, gaps: [] };
        }
      }

      const { data: calendarRows, error } = await calQuery;
      if (error) {
        return { success: false, error: error.message };
      }

      const byRoomCode = new Map<string, CalendarAvailabilityRow[]>();
      for (const row of calendarRows || []) {
        if (!row.room_type_code) continue;
        const bucket = byRoomCode.get(row.room_type_code);
        if (bucket) bucket.push(row);
        else byRoomCode.set(row.room_type_code, [row]);
      }

      const allGaps: ReservationGap[] = [];
      for (const [code, rows] of byRoomCode) {
        const rtId = roomCodeToId.get(code);
        if (!rtId) {
          console.log(
            `[GapDetectionService] No room_type mapping for code ${code} — skipping availability rows`
          );
          continue;
        }
        allGaps.push(
          ...this.findGapsByAvailability(
            rows,
            propertyId,
            rtId,
            minGapDays,
            maxGapDays
          )
        );
      }

      // Calculate suggested discounts for each gap
      await this.calculateGapStrategies(allGaps);

      // Save gaps to database
      await this.saveGapsToDatabase(allGaps);

      // Reconcile: expire identified gaps that no longer exist between active reservations
      await this.reconcileGaps(propertyId, allGaps, endDate);

      return { success: true, gaps: allGaps };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Find gaps anchored on availability (sold-out days) for one room type.
   *
   * A gap is a run of OPEN dates (`available_rooms > 0`) strictly between
   * two sold-out dates (`available_rooms === 0`) that are consecutive in
   * calendar coverage (no missing days in between). Matches the pattern
   * `[sold-out, open*X, sold-out]` with `X` within `[minDays, maxDays]`.
   *
   * Convention (matches reservation-based detector for downstream consumers):
   *   - `startDate` = first open day (= left sold-out + 1)
   *   - `endDate`   = next sold-out (exclusive boundary)
   *   - `duration`  = X open nights
   */
  private findGapsByAvailability(
    rows: CalendarAvailabilityRow[],
    propertyId: string,
    roomTypeId: string,
    minDays: number,
    maxDays: number
  ): ReservationGap[] {
    const gaps: ReservationGap[] = [];
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const DAY_MS = 1000 * 60 * 60 * 24;

    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].available_rooms !== 0) continue;

      // Walk forward day-by-day looking for the next sold-out anchor. Break
      // continuity if calendar coverage skips a date — we don't fabricate
      // open days we never observed.
      const leftDate = new Date(sorted[i].date);
      let expected = new Date(leftDate.getTime() + DAY_MS);
      let openCount = 0;
      let rightIdx = -1;

      for (let j = i + 1; j < sorted.length; j++) {
        const rowDate = new Date(sorted[j].date);
        if (rowDate.getTime() !== expected.getTime()) break;
        if (sorted[j].available_rooms === 0) {
          rightIdx = j;
          break;
        }
        openCount++;
        if (openCount > maxDays) break;
        expected = new Date(expected.getTime() + DAY_MS);
      }

      if (rightIdx !== -1 && openCount >= minDays && openCount <= maxDays) {
        const gapStart = new Date(leftDate.getTime() + DAY_MS);
        const gapEnd = new Date(sorted[rightIdx].date);
        const surroundingRate =
          sorted[i].final_rate ?? sorted[rightIdx].final_rate ?? 100;
        gaps.push({
          id: `gap-${propertyId}-${roomTypeId}-${gapStart.toISOString()}`,
          propertyId,
          roomTypeId,
          startDate: gapStart,
          endDate: gapEnd,
          duration: openCount,
          suggestedDiscount: 0,
          discountAmount: 0,
          originalPrice: surroundingRate,
          discountedPrice: surroundingRate,
          status: 'identified',
          // Stamp the detection time so freshly-detected (not-yet-persisted)
          // gaps carry a valid date for the UI. Without it the panel did
          // `new Date(undefined)` -> Invalid Date -> RangeError crash.
          detectedAt: new Date(),
          notes: `Gap between sold-out anchors ${sorted[i].date} → ${sorted[rightIdx].date}`,
        });
        // Let the next iteration pick up at the right sold-out anchor so
        // we can chain consecutive gaps (sold-out → open → sold-out → open
        // → sold-out produces two gaps).
        i = rightIdx - 1;
      }
    }

    return gaps;
  }

  /**
   * Find gaps in a sorted list of reservations
   */
  private findGapsInReservations(
    reservations: ReservationRecord[],
    propertyId: string,
    roomTypeId: string,
    minDays: number,
    maxDays: number
  ): ReservationGap[] {
    const gaps: ReservationGap[] = [];

    // Sort by check-in date
    const sorted = [...reservations].sort(
      (a, b) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime()
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      const gapStart = new Date(current.check_out);
      const gapEnd = new Date(next.check_in);

      // Calculate gap duration in days
      const duration = Math.floor(
        (gapEnd.getTime() - gapStart.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Check if gap is within our criteria
      if (duration >= minDays && duration <= maxDays) {
        gaps.push({
          id: `gap-${propertyId}-${roomTypeId}-${gapStart.toISOString()}`,
          propertyId,
          roomTypeId,
          startDate: gapStart,
          endDate: gapEnd,
          duration,
          suggestedDiscount: 0, // Will be calculated
          discountAmount: 0,
          originalPrice: current.rate, // Use surrounding rate as baseline
          discountedPrice: current.rate,
          status: 'identified',
          notes: `Gap between reservations ${current.id} and ${next.id}`,
        });
      }
    }

    return gaps;
  }

  /**
   * Calculate gap filling strategies based on rules and room config
   */
  private async calculateGapStrategies(gaps: ReservationGap[]): Promise<void> {
    for (const gap of gaps) {
      try {
        // Skip gaps with invalid roomTypeId
        if (
          !gap.roomTypeId ||
          gap.roomTypeId === 'null' ||
          gap.roomTypeId === 'undefined'
        ) {
          console.warn(
            `[GapDetectionService] Skipping gap ${gap.id} - invalid roomTypeId: ${gap.roomTypeId}`
          );
          continue;
        }

        // Get gap filling rules for this property
        const rules = await this.getGapFillingRules(gap.propertyId);

        // Get room type configuration
        const { data: roomType } = await supabase
          .from('room_types')
          .select('gap_discount_rate, min_gap_discount, max_gap_discount')
          .eq('id', gap.roomTypeId)
          .single();

        if (!roomType) {
          console.warn(
            `[GapDetectionService] Room type not found for gap ${gap.id}, roomTypeId: ${gap.roomTypeId}`
          );
          continue;
        }

        // Find applicable rule
        const applicableRule = rules.find(
          (r) =>
            r.active &&
            gap.duration >= r.gapDuration.min &&
            gap.duration <= r.gapDuration.max
        );

        let suggestedDiscount: number;

        if (applicableRule) {
          // Use rule-based discount
          suggestedDiscount = applicableRule.discountRate;
        } else {
          // Use room type default discount
          suggestedDiscount = roomType.gap_discount_rate || 15;
        }

        gap.suggestedDiscountRate = suggestedDiscount;

        const minDiscount = roomType.min_gap_discount || 5;
        suggestedDiscount = Math.max(minDiscount, suggestedDiscount);

        // Calculate prices using original price from gap (current WebHotelier price)
        const currentPrice = gap.originalPrice || 100;
        const discountAmount = currentPrice * (suggestedDiscount / 100);
        const discountedPrice = currentPrice - discountAmount;

        // Update gap
        gap.suggestedDiscount = suggestedDiscount;
        gap.discountAmount = discountAmount;
        gap.originalPrice = currentPrice;
        gap.discountedPrice = discountedPrice;
        if (applicableRule) {
          gap.ruleAppliedId = applicableRule.id;
        }
      } catch (error) {
        console.error(`Failed to calculate strategy for gap ${gap.id}:`, error);
      }
    }
  }

  /**
   * Get gap filling rules for a property
   */
  private async getGapFillingRules(
    propertyId: string
  ): Promise<GapFillingRule[]> {
    try {
      const { data, error } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('property_id', propertyId)
        .eq('rule_type', 'gap_filling')
        .eq('active', true);

      if (error || !data) {
        return [];
      }

      // Transform to GapFillingRule format. Gap-detection suggestions historically
      // expressed a positive-magnitude discount. After legacy cleanup, we derive
      // the magnitude from the signed price_adjustment.value only when it's a
      // discount (negative); positive premiums don't map to "discountRate" so
      // we fall back to the conservative default of 15.
      return data.map((rule) => {
        const signedValue = rule.actions?.price_adjustment?.value as
          | number
          | undefined;
        const discountRate =
          signedValue !== undefined && signedValue < 0
            ? Math.abs(signedValue)
            : 15;
        return {
          id: rule.id,
          gapDuration: {
            min: rule.conditions?.gap_min || 1,
            max: rule.conditions?.gap_max || 7,
          },
          discountRate,
          conditions: rule.conditions,
          active: rule.active,
        };
      });
    } catch (error) {
      console.error('Failed to load gap filling rules:', error);
      return [];
    }
  }

  /**
   * Save detected gaps to database
   */
  private async saveGapsToDatabase(gaps: ReservationGap[]): Promise<void> {
    try {
      for (const gap of gaps) {
        // Skip gaps with invalid roomTypeId
        if (
          !gap.roomTypeId ||
          gap.roomTypeId === 'null' ||
          gap.roomTypeId === 'undefined'
        ) {
          console.warn(
            `[GapDetectionService] Not saving gap ${gap.id} - invalid roomTypeId: ${gap.roomTypeId}`
          );
          continue;
        }

        // Check if gap already exists
        const { data: existing } = await supabase
          .from('reservation_gaps')
          .select('id')
          .eq('property_id', gap.propertyId)
          .eq('room_type_id', gap.roomTypeId)
          .eq('start_date', gap.startDate.toISOString().split('T')[0])
          .eq('status', 'identified')
          .maybeSingle();

        if (existing) {
          // Update existing gap
          await supabase
            .from('reservation_gaps')
            .update({
              end_date: gap.endDate.toISOString().split('T')[0],
              duration: gap.duration,
              suggested_discount: gap.suggestedDiscount,
              suggested_discount_rate: gap.suggestedDiscountRate,
              discount_amount: gap.discountAmount,
              original_price: gap.originalPrice,
              discounted_price: gap.discountedPrice,
              rule_applied_id: gap.ruleAppliedId,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
        } else {
          // Insert new gap
          await supabase.from('reservation_gaps').insert({
            property_id: gap.propertyId,
            room_type_id: gap.roomTypeId,
            start_date: gap.startDate.toISOString().split('T')[0],
            end_date: gap.endDate.toISOString().split('T')[0],
            duration: gap.duration,
            suggested_discount: gap.suggestedDiscount,
            suggested_discount_rate: gap.suggestedDiscountRate,
            discount_amount: gap.discountAmount,
            original_price: gap.originalPrice,
            discounted_price: gap.discountedPrice,
            rule_applied_id: gap.ruleAppliedId,
            detected_at: new Date().toISOString(),
            status: 'identified',
            notes: gap.notes,
          });
        }
      }
    } catch (error) {
      console.error('Failed to save gaps to database:', error);
    }
  }

  /**
   * Reconcile gaps: expire any "identified" gaps that no longer exist between active reservations
   */
  private async reconcileGaps(
    propertyId: string,
    validGaps: ReservationGap[],
    lookAheadEndDate?: Date
  ): Promise<void> {
    try {
      const validKeys = new Set(
        validGaps.map(
          (g) => `${g.roomTypeId}:${g.startDate.toISOString().split('T')[0]}`
        )
      );

      const todayStr = new Date().toISOString().split('T')[0];
      let query = supabase
        .from('reservation_gaps')
        .select('id, room_type_id, start_date')
        .eq('property_id', propertyId)
        .eq('status', 'identified')
        .gte('end_date', todayStr);

      if (lookAheadEndDate) {
        query = query.lte(
          'start_date',
          lookAheadEndDate.toISOString().split('T')[0]
        );
      }

      const { data: existingGaps, error } = await query;

      if (error || !existingGaps) return;

      let expiredCount = 0;
      for (const existing of existingGaps) {
        const key = `${existing.room_type_id}:${existing.start_date}`;
        if (!validKeys.has(key)) {
          await supabase
            .from('reservation_gaps')
            .update({
              status: 'expired',
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
          expiredCount++;
        }
      }

      if (expiredCount > 0) {
        console.log(
          `[GapDetectionService] Reconciled: expired ${expiredCount} phantom gaps (in-window)`
        );
      }

      // Second pass: reconcile out-of-window gaps by verifying bounding reservations
      if (lookAheadEndDate) {
        const endDateStr = lookAheadEndDate.toISOString().split('T')[0];
        const { data: outOfWindowGaps } = await supabase
          .from('reservation_gaps')
          .select('id, room_type_id, start_date, end_date')
          .eq('property_id', propertyId)
          .eq('status', 'identified')
          .gt('start_date', endDateStr);

        if (outOfWindowGaps && outOfWindowGaps.length > 0) {
          // Build reverse mapping: room_type_id (UUID) -> webhotelier_code
          const { data: roomTypes } = await supabase
            .from('room_types')
            .select('id, webhotelier_code')
            .eq('property_id', propertyId);

          const roomIdToCode = new Map<string, string>();
          if (roomTypes) {
            for (const rt of roomTypes) {
              if (rt.webhotelier_code) {
                roomIdToCode.set(rt.id, rt.webhotelier_code);
              }
            }
          }

          let outOfWindowExpired = 0;
          for (const gap of outOfWindowGaps) {
            const roomCode = roomIdToCode.get(gap.room_type_id);
            if (!roomCode) {
              await supabase
                .from('reservation_gaps')
                .update({
                  status: 'expired',
                  updated_at: new Date().toISOString(),
                })
                .eq('id', gap.id);
              outOfWindowExpired++;
              continue;
            }

            const { data: leftRes } = await supabase
              .from('reservations')
              .select('id')
              .eq('property_id', propertyId)
              .eq('webhotelier_room_code', roomCode)
              .eq('check_out', gap.start_date)
              .in('status', ['confirmed', 'checked_in'])
              .limit(1);

            const { data: rightRes } = await supabase
              .from('reservations')
              .select('id')
              .eq('property_id', propertyId)
              .eq('webhotelier_room_code', roomCode)
              .eq('check_in', gap.end_date)
              .in('status', ['confirmed', 'checked_in'])
              .limit(1);

            if (!leftRes?.length || !rightRes?.length) {
              await supabase
                .from('reservation_gaps')
                .update({
                  status: 'expired',
                  updated_at: new Date().toISOString(),
                })
                .eq('id', gap.id);
              outOfWindowExpired++;
            }
          }

          if (outOfWindowExpired > 0) {
            console.log(
              `[GapDetectionService] Reconciled: expired ${outOfWindowExpired} phantom gaps (out-of-window)`
            );
          }
        }
      }
    } catch (error) {
      console.error('[GapDetectionService] Error reconciling gaps:', error);
    }
  }

  /**
   * Return the identified gap that fully encloses a prospective stay, with its
   * length in nights. Used by the MinLOS resolver to decide whether a short
   * stay may relax the minimum-stay restriction to fill an orphan gap.
   */
  async getEnclosingGap(
    propertyId: string,
    roomTypeId: string,
    checkIn: Date,
    checkOut: Date
  ): Promise<{ id: string; durationNights: number } | null> {
    try {
      const { data: gap } = await supabase
        .from('reservation_gaps')
        .select('id, duration')
        .eq('property_id', propertyId)
        .eq('room_type_id', roomTypeId)
        .lte('start_date', checkIn.toISOString().split('T')[0])
        .gte('end_date', checkOut.toISOString().split('T')[0])
        .eq('status', 'identified')
        .maybeSingle();

      if (!gap) return null;
      return { id: gap.id, durationNights: gap.duration };
    } catch (error) {
      console.error('Failed to load enclosing gap:', error);
      return null;
    }
  }

  /**
   * Mark a gap as filled
   */
  async markGapFilled(
    gapId: string,
    reservationId: string,
    revenueCaptured: number
  ): Promise<void> {
    try {
      await supabase
        .from('reservation_gaps')
        .update({
          status: 'filled',
          filled_at: new Date().toISOString(),
          filled_reservation_id: reservationId,
          revenue_impact: revenueCaptured,
        })
        .eq('id', gapId);
    } catch (error) {
      console.error('Failed to mark gap as filled:', error);
    }
  }

  /**
   * Get gap statistics for a property
   */
  async getGapStatistics(
    propertyId: string,
    dateRange?: { start: Date; end: Date }
  ): Promise<{
    totalGaps: number;
    identifiedGaps: number;
    filledGaps: number;
    ignoredGaps: number;
    fillRate: number;
    averageDuration: number;
    totalRevenueCaptured: number;
  }> {
    try {
      let query = supabase
        .from('reservation_gaps')
        .select('*')
        .eq('property_id', propertyId);

      if (dateRange) {
        query = query
          .gte('start_date', dateRange.start.toISOString().split('T')[0])
          .lte('end_date', dateRange.end.toISOString().split('T')[0]);
      }

      const { data, error } = await query;

      if (error || !data) {
        return {
          totalGaps: 0,
          identifiedGaps: 0,
          filledGaps: 0,
          ignoredGaps: 0,
          fillRate: 0,
          averageDuration: 0,
          totalRevenueCaptured: 0,
        };
      }

      const totalGaps = data.length;
      const identifiedGaps = data.filter(
        (g) => g.status === 'identified'
      ).length;
      const filledGaps = data.filter((g) => g.status === 'filled').length;
      const ignoredGaps = data.filter((g) => g.status === 'ignored').length;
      const fillRate = totalGaps > 0 ? (filledGaps / totalGaps) * 100 : 0;
      const averageDuration =
        data.reduce((sum, g) => sum + g.duration, 0) / (totalGaps || 1);
      const totalRevenueCaptured = data
        .filter((g) => g.status === 'filled')
        .reduce((sum, g) => sum + (g.revenue_impact || 0), 0);

      return {
        totalGaps,
        identifiedGaps,
        filledGaps,
        ignoredGaps,
        fillRate,
        averageDuration,
        totalRevenueCaptured,
      };
    } catch (error) {
      console.error('Failed to get gap statistics:', error);
      return {
        totalGaps: 0,
        identifiedGaps: 0,
        filledGaps: 0,
        ignoredGaps: 0,
        fillRate: 0,
        averageDuration: 0,
        totalRevenueCaptured: 0,
      };
    }
  }

  /**
   * Update the status of a gap
   */
  async updateGapStatus(
    gapId: string,
    newStatus: 'identified' | 'filled' | 'ignored' | 'expired'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('reservation_gaps')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', gapId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: unknown) {
      console.error('[GapDetectionService] Error updating gap status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Apply a discount to a gap
   */
  async applyGapDiscount(
    gapId: string,
    discountRate: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: gap, error: fetchError } = await supabase
        .from('reservation_gaps')
        .select('*')
        .eq('id', gapId)
        .single();

      if (fetchError || !gap) {
        return {
          success: false,
          error: fetchError?.message || 'Gap not found',
        };
      }

      // Look up the room_type_code for this gap's room_type_id (UUID)
      const { data: roomType } = await supabase
        .from('room_types')
        .select('webhotelier_code')
        .eq('id', gap.room_type_id)
        .single();

      if (!roomType?.webhotelier_code) {
        return {
          success: false,
          error: `No room type mapping found for ${gap.room_type_id}`,
        };
      }

      const { data: calendarRows, error: calendarError } = await supabase
        .from('calendar_data')
        .select('property_id, room_type_code, date, base_rate, final_rate')
        .eq('property_id', gap.property_id)
        .eq('room_type_code', roomType.webhotelier_code)
        .gte('date', gap.start_date)
        .lte('date', gap.end_date);

      if (calendarError) {
        return { success: false, error: calendarError.message };
      }

      // Gap filling calculates the discounted price only.
      // Price limits (min/max) are enforced globally by the pricing engine as a final safeguard.
      const updates = [];
      for (const row of calendarRows || []) {
        const baseRate = row.base_rate || row.final_rate || 100;
        const discountedPrice = Math.round(baseRate * (1 - discountRate));
        updates.push({
          property_id: row.property_id,
          room_type_code: row.room_type_code,
          date: row.date,
          final_rate: discountedPrice,
          updated_at: new Date().toISOString(),
        });
      }

      if (updates.length > 0) {
        const { error: updateError } = await supabase
          .from('calendar_data')
          .upsert(updates, {
            onConflict: 'property_id,room_type_code,date',
          });

        if (updateError) {
          return { success: false, error: updateError.message };
        }
      }

      const { error: gapUpdateError } = await supabase
        .from('reservation_gaps')
        .update({
          status: 'filled',
          discount_applied: discountRate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', gapId);

      if (gapUpdateError) {
        return { success: false, error: gapUpdateError.message };
      }

      return { success: true };
    } catch (error: unknown) {
      console.error(
        '[GapDetectionService] Error applying gap discount:',
        error
      );
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get all active (identified) gaps for a property
   * Used by pricing engine to know which dates should get gap discounts
   */
  async getActiveGaps(
    propertyId: string,
    roomTypeId?: string
  ): Promise<ReservationGap[]> {
    try {
      let query = supabase
        .from('reservation_gaps')
        .select('*')
        .eq('property_id', propertyId)
        .eq('status', 'identified')
        .gte('end_date', new Date().toISOString().split('T')[0]);

      if (roomTypeId) {
        query = query.eq('room_type_id', roomTypeId);
      }

      const { data, error } = await query;

      if (error || !data) {
        console.error(
          '[GapDetectionService] Error fetching active gaps:',
          error
        );
        return [];
      }

      return data.map((gap) => ({
        id: gap.id,
        propertyId: gap.property_id,
        roomTypeId: gap.room_type_id,
        startDate: new Date(gap.start_date),
        endDate: new Date(gap.end_date),
        duration: gap.duration,
        suggestedDiscount: gap.suggested_discount || 0,
        suggestedDiscountRate: gap.suggested_discount_rate,
        discountAmount: gap.discount_amount || 0,
        originalPrice: gap.original_price || 0,
        discountedPrice: gap.discounted_price || 0,
        status: gap.status,
        strategy: gap.strategy,
        ruleAppliedId: gap.rule_applied_id,
        detectedAt: gap.detected_at ? new Date(gap.detected_at) : undefined,
        notes: gap.notes,
      }));
    } catch (error) {
      console.error('[GapDetectionService] Error in getActiveGaps:', error);
      return [];
    }
  }

  /**
   * Check if a specific date falls within an identified gap
   * Returns the gap if found, null otherwise
   */
  async isDateInGap(
    propertyId: string,
    roomTypeId: string,
    date: Date
  ): Promise<ReservationGap | null> {
    try {
      const dateStr = date.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('reservation_gaps')
        .select('*')
        .eq('property_id', propertyId)
        .eq('room_type_id', roomTypeId)
        .eq('status', 'identified')
        .lte('start_date', dateStr)
        .gte('end_date', dateStr)
        .limit(1)
        .single();

      if (error || !data) {
        return null;
      }

      return {
        id: data.id,
        propertyId: data.property_id,
        roomTypeId: data.room_type_id,
        startDate: new Date(data.start_date),
        endDate: new Date(data.end_date),
        duration: data.duration,
        suggestedDiscount: data.suggested_discount || 0,
        suggestedDiscountRate: data.suggested_discount_rate,
        discountAmount: data.discount_amount || 0,
        originalPrice: data.original_price || 0,
        discountedPrice: data.discounted_price || 0,
        status: data.status,
        strategy: data.strategy,
        ruleAppliedId: data.rule_applied_id,
        detectedAt: data.detected_at ? new Date(data.detected_at) : undefined,
        notes: data.notes,
      };
    } catch (error) {
      console.error('[GapDetectionService] Error in isDateInGap:', error);
      return null;
    }
  }

  /**
   * Get gaps that overlap with a date range (for batch processing)
   * Returns a map of dateString -> gap for quick lookups
   */
  async getGapsForDateRange(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    roomTypeId?: string
  ): Promise<Map<string, ReservationGap>> {
    const gapsMap = new Map<string, ReservationGap>();

    try {
      let query = supabase
        .from('reservation_gaps')
        .select('*')
        .eq('property_id', propertyId)
        .eq('status', 'identified')
        .lte('start_date', endDate.toISOString().split('T')[0])
        .gte('end_date', startDate.toISOString().split('T')[0]);

      if (roomTypeId) {
        query = query.eq('room_type_id', roomTypeId);
      }

      const { data, error } = await query;

      if (error || !data) {
        return gapsMap;
      }

      // Build a lookup map: for each gap, add entries for all dates within the gap
      for (const gapData of data) {
        const gap: ReservationGap = {
          id: gapData.id,
          propertyId: gapData.property_id,
          roomTypeId: gapData.room_type_id,
          startDate: new Date(gapData.start_date),
          endDate: new Date(gapData.end_date),
          duration: gapData.duration,
          suggestedDiscount: gapData.suggested_discount || 0,
          suggestedDiscountRate: gapData.suggested_discount_rate,
          discountAmount: gapData.discount_amount || 0,
          originalPrice: gapData.original_price || 0,
          discountedPrice: gapData.discounted_price || 0,
          status: gapData.status,
          strategy: gapData.strategy,
          ruleAppliedId: gapData.rule_applied_id,
          detectedAt: gapData.detected_at
            ? new Date(gapData.detected_at)
            : undefined,
          notes: gapData.notes,
        };

        // Add entry for each date in the gap
        const gapStart = new Date(gapData.start_date);
        const gapEnd = new Date(gapData.end_date);
        for (
          let d = new Date(gapStart);
          d < gapEnd;
          d.setDate(d.getDate() + 1)
        ) {
          const key = `${gapData.room_type_id}-${d.toISOString().split('T')[0]}`;
          gapsMap.set(key, gap);
        }
      }

      return gapsMap;
    } catch (error) {
      console.error(
        '[GapDetectionService] Error in getGapsForDateRange:',
        error
      );
      return gapsMap;
    }
  }
}

// Export singleton instance
export const gapDetectionService = new GapDetectionService();
